import { get } from '@core/request';

// ── 字典缓存 ──────────────────────────────────────────────────────────────────

let cachedPostCodeOrderMap = null;

/**
 * DFS 遍历字典树，按访问顺序收集所有节点 code
 * 同时处理 labels（分类组）和 options（具体条目）两种子节点形式
 */
function collectAllCodesDFS(node, result) {
    if (!node || !node.code) return;
    result.push(node.code);
    for (const child of node.labels || []) {
        collectAllCodesDFS(child, result);
    }
    for (const child of node.options || []) {
        collectAllCodesDFS(child, result);
    }
}

/**
 * 从认证字典接口响应构建 Map<code, index>，index 越小优先级越高
 */
function buildPostCodeOrderMap(dictRoot) {
    const codes = [];
    collectAllCodesDFS(dictRoot, codes);
    const map = new Map();
    codes.forEach((code, index) => map.set(code, index));
    return map;
}

/**
 * 加载个人专业认证字典，返回 postCodeOrderMap。
 * 结果缓存在模块级变量，多次调用只请求一次接口。
 * 接口失败时返回空 Map（职务维度全部降级到最低优先级）。
 */
export async function getPostCodeOrderMap() {
    if (cachedPostCodeOrderMap !== null) return cachedPostCodeOrderMap;
    try {
        const res = await get('/dataengine/api/authdictionary/json/ex');
        // 兼容直接响应 { code: 'A', ... } 和包装响应 { data: { code: 'A', ... } }
        const dictRoot = res?.data?.code ? res.data : res?.code ? res : null;
        if (dictRoot) {
            cachedPostCodeOrderMap = buildPostCodeOrderMap(dictRoot);
        } else {
            cachedPostCodeOrderMap = new Map();
        }
    } catch (err) {
        console.warn('认证字典加载失败，职务排序维度降级：', err);
        cachedPostCodeOrderMap = new Map();
    }
    return cachedPostCodeOrderMap;
}

// ── 各维度优先级计算 ──────────────────────────────────────────────────────────

/**
 * D0：两院院士优先级
 * 取第一个工作经历的 unitName + unitPost（trim 后拼接），
 * 匹配"中国科学院院士"（index 0）或"中国工程院院士"（index 1）按下标排序，未命中返回数组长度。
 */
const ACADEMICIAN_SORT_ORDER = ['中国科学院院士', '中国工程院院士'];

export function getAcademicianPriority(item) {
    const exp = item.workExperiences?.[0];
    if (!exp) return ACADEMICIAN_SORT_ORDER.length;
    const unitName = (exp.unitName ?? '').trim();
    const unitPost = (exp.unitPost ?? '').trim();
    const idx = ACADEMICIAN_SORT_ORDER.indexOf(unitName + unitPost);
    return idx === -1 ? ACADEMICIAN_SORT_ORDER.length : idx;
}

/**
 * D1：机构类型优先级
 * 政府(2) > 学协会(3) > 医疗机构(1) > 企业(5) > 科研院校(4) > 未分类(0)/无数据
 */
const ORG_TYPE_PRIORITY = { 2: 0, 3: 1, 1: 2, 5: 3, 4: 4, 0: 5 };

export function getOrgTypePriority(item) {
    const orgType = item.workExperiences?.[0]?.organisation?.orgType;
    if (orgType === undefined || orgType === null) return 5;
    return ORG_TYPE_PRIORITY[orgType] ?? 5;
}

/**
 * D2a：医疗机构级别优先级（仅 orgType===1 时有效，其他类型返回 0 以忽略此维度）
 * 三级 > 二级 > 一级 > 其他
 */
const MED_LEVEL_PRIORITY = { 三级: 0, 二级: 1, 一级: 2 };

export function getMedLevelPriority(item) {
    const orgType = item.workExperiences?.[0]?.organisation?.orgType;
    if (orgType !== 1) return 0;
    const medLevel = item.workExperiences?.[0]?.organisation?.medLevel;
    return MED_LEVEL_PRIORITY[medLevel] ?? 3;
}

/**
 * D2b：医疗机构等次优先级（仅 orgType===1 时有效，其他类型返回 0 以忽略此维度）
 * 甲等 > 乙等 > 丙等 > 其他
 */
const MED_GRADE_PRIORITY = { 甲等: 0, 乙等: 1, 丙等: 2 };

export function getMedGradePriority(item) {
    const orgType = item.workExperiences?.[0]?.organisation?.orgType;
    if (orgType !== 1) return 0;
    const medGrade = item.workExperiences?.[0]?.organisation?.medGrade;
    return MED_GRADE_PRIORITY[medGrade] ?? 3;
}

/**
 * D3：专家级别优先级
 * 6 > 5 > 4 > 3 > 2 > 1 > 9 > 其他
 */
const EXPERT_LEVEL_PRIORITY = { 6: 0, 5: 1, 4: 2, 3: 3, 2: 4, 1: 5, 9: 6 };

export function getExpertLevelPriority(item) {
    return EXPERT_LEVEL_PRIORITY[item.expertLevel] ?? 7;
}

/**
 * D4：身份类型（memPostCode 前缀）优先级
 * A02 政府官员 > A01 医疗机构从业者 > A04 教育科研/社会团体 > A03 商业人士
 */
const POST_CODE_PREFIX_ORDER = { A02: 0, A01: 1, A04: 2, A03: 3 };

export function getPostPrefixPriority(item) {
    const memPostCode = item.workExperiences?.[0]?.memPostCode;
    if (!memPostCode) return 999;
    const prefix = memPostCode.split('-')[0];
    return POST_CODE_PREFIX_ORDER[prefix] ?? 999;
}

/**
 * D5：职务编码（memPostCode）在字典中的位置，位置越靠前优先级越高
 */
export function getPostCodePriority(item, postCodeOrderMap) {
    const memPostCode = item.workExperiences?.[0]?.memPostCode;
    if (!memPostCode || !postCodeOrderMap?.size) return 999999;
    return postCodeOrderMap.get(memPostCode) ?? 999999;
}

/**
 * D6：姓名拼音，空字符串/null 排到最后
 */
export function getPinyinSortKey(item) {
    return item.namePinyin || '\uffff';
}

// ── 主排序函数 ────────────────────────────────────────────────────────────────

/**
 * computeAutoSort
 *
 * 对 tableData 按 6 维优先级进行稳定升序排序，返回新数组。
 * 每条记录的 sort 字段替换为从 1 开始的连续整数。
 *
 * @param {Array} tableData - 来自 SeatingArrangementModal 的表格行数据
 * @param {Map} postCodeOrderMap - 由 getPostCodeOrderMap() 返回的字典索引
 * @returns {Array} 排序后的新数组（不修改原数组）
 */
export function computeAutoSort(tableData, postCodeOrderMap = new Map()) {
    const sorted = [...tableData].sort((a, b) => {
        // D0 两院院士
        const d0 = getAcademicianPriority(a) - getAcademicianPriority(b);
        if (d0 !== 0) return d0;
        // D1 机构类型
        const d1 = getOrgTypePriority(a) - getOrgTypePriority(b);
        if (d1 !== 0) return d1;
        // D2a 机构级别
        const d2a = getMedLevelPriority(a) - getMedLevelPriority(b);
        if (d2a !== 0) return d2a;
        // D2b 机构等次
        const d2b = getMedGradePriority(a) - getMedGradePriority(b);
        if (d2b !== 0) return d2b;
        // D3 专家级别
        const d3 = getExpertLevelPriority(a) - getExpertLevelPriority(b);
        if (d3 !== 0) return d3;
        // D4 身份类型前缀
        const d4 = getPostPrefixPriority(a) - getPostPrefixPriority(b);
        if (d4 !== 0) return d4;
        // D5 职务编码
        const d5 = getPostCodePriority(a, postCodeOrderMap) - getPostCodePriority(b, postCodeOrderMap);
        if (d5 !== 0) return d5;
        // D6 姓名拼音
        const pa = getPinyinSortKey(a);
        const pb = getPinyinSortKey(b);
        if (pa < pb) return -1;
        if (pa > pb) return 1;
        return 0;
    });
    return sorted.map((item, index) => ({ ...item, sort: index + 1 }));
}
